export const API_BASE = process.env.REACT_APP_API_URL || "";

const CSRF_COOKIE = "XSRF-TOKEN";
const CSRF_HEADER = "X-XSRF-TOKEN";

/**
 * The anti-forgery token the backend issues alongside the session.
 *
 * Deliberately readable by script, unlike the session cookie - it isn't a secret, and
 * echoing it back in a header is the whole mechanism: an attacker's page can make the
 * browser send our cookies, but can't read them to construct this header.
 */
function csrfToken(): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
    // POST is the only mutating method this app uses, and it's the only one the server
    // guards. Sending the header when there's no cookie would just be noise.
    const token = options.method === "POST" ? csrfToken() : null;

    const res = await fetch(`${API_BASE}${path}`, {
        credentials: "include",
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...(token ? { [CSRF_HEADER]: token } : {}),
            ...(options.headers || {}),
        },
    });

    if (!res.ok) {
        let message = `HTTP error! status: ${res.status}`;
        try {
            const data = await res.clone().json();
            // Several endpoints send both a short `error` label and a more specific
            // `message` (e.g. "Playlist limit reached" + "You can have up to 3...") -
            // prefer the specific one when it's there.
            if (data?.message) {
                message = data.message;
            } else if (data?.error) {
                message = data.error;
            }
        } catch {
            // Response wasn't JSON - keep the generic status message.
        }
        throw new Error(message);
    }

    return res;
}

export async function apiGet<T>(path: string): Promise<T> {
    const res = await apiFetch(path, { method: "GET" });
    return res.json();
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
    const res = await apiFetch(path, {
        method: "POST",
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return res.json();
}
